import React from 'react';
import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import { simpleDateAtTime } from 'lib/date';
import { capitalize } from 'lib/util';
import { ReviewStatus } from 'models/Review';
import Referral from 'models/reviews/Referral';
import colors from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import NoteIcon from 'svg/NoteIcon';

const columnHelper = createColumnHelper<Referral>();

type Props = {
  onClickReview: (referralId: string) => void;
  isMobile: boolean;
};

export default function referralsTableColumns({ onClickReview, isMobile }: Props) {
  return [
    columnHelper.accessor((row) => row.hasNewChanges, {
      header: '',
      id: 'newLogsFlag',
      cell: (info) => info.getValue() && <NoteIcon color={colors.blue100} width={16} height={16} />,
      size: 16,
      meta: {
        style: {
          ...(isMobile ? { paddingLeft: '10px' } : {}),
        },
      },
    }),
    columnHelper.accessor((row) => row.patient?.name, {
      id: 'patient',
      header: 'Patient Name',
      cell: (info) => (
        <Link
          fontWeight='500'
          onClick={() => onClickReview(info.row.original.id)}
          data-dd-action-name='Patient name'
          data-cy='referralPatientName'>
          {info.getValue()}
        </Link>
      ),
      size: 100,
    }),
    columnHelper.accessor((row) => row.planTypeClassification, {
      id: 'planTypeClassification',
      header: 'Plan Type',
      cell: (info) => info.getValue()?.name || '—',
      size: 100,
    }),
    columnHelper.accessor((row) => row.referredBy, {
      id: 'referredBy',
      header: 'Referred By',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return '—';
        return <GroupCell name={value.name} typeName={value.groupType?.displayName} />;
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.group, {
      id: 'group',
      header: 'Referred To',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return '—';
        return <GroupCell name={value.name} typeName={value.groupType?.displayName} data-cy='referredToGroup' />;
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.createdAt, {
      id: 'createdAt',
      header: 'Date/Time Received',
      cell: (info) => (info.getValue() ? simpleDateAtTime(info.getValue() ?? '') : '—'),
      size: 100,
    }),
    columnHelper.accessor((row) => row.status, {
      id: 'status',
      header: 'Status',
      cell: (info) => (
        <StatusChip $status={info.row.original.status}>
          {info.row.original.rejected ? 'Denied' : capitalize(info.row.original.status)}
        </StatusChip>
      ),
      size: 60,
    }),
    columnHelper.accessor((row) => row.dischargedTo, {
      id: 'dischargedTo',
      header: 'Discharged To',
      cell: (info) => {
        const value = info.getValue();
        if (!value) return null;
        return <GroupCell name={value.name} typeName={value.groupType?.displayName} data-testid='dischargedTo' />;
      },
      size: 140,
    }),
  ];
}

function GroupCell({
  name,
  typeName,
  ...rest
}: {
  name: string;
  typeName?: string;
  'data-cy'?: string;
  'data-testid'?: string;
}) {
  return (
    <GroupCellContainer {...rest}>
      <span>{name}</span>
      {typeName && <SubLabel>{typeName}</SubLabel>}
    </GroupCellContainer>
  );
}

const GroupCellContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: ${({ theme }) => theme.textStyles.label.fontSize};
  line-height: ${({ theme }) => theme.textStyles.label.lineHeight};
`;

const Link = styled(BodySmall)`
  cursor: pointer;
  color: ${() => colors.blue100};
`;

export const StatusChip = styled(BodySmall)<{ $status: ReviewStatus }>`
  display: inline-block;
  width: fit-content;
  background-color: ${(props) =>
    ({
      [ReviewStatus.PENDING]: 'var(--black-10)',
      [ReviewStatus.ACCEPTED]: 'var(--accent-green)',
      [ReviewStatus.REJECTED]: 'var(--accent-red)',
      [ReviewStatus.CANCELED]: 'var(--black-75)',
    })[props.$status]};
  color: ${(props) =>
    ({
      [ReviewStatus.PENDING]: 'var(--black-75)',
      [ReviewStatus.ACCEPTED]: 'var(--white)',
      [ReviewStatus.REJECTED]: 'var(--white)',
      [ReviewStatus.CANCELED]: 'var(--white)',
    })[props.$status]};
  border-radius: 28px;
  white-space: nowrap;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`;
